import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { VaultErrorSection } from '../session/VaultErrorSection';
import { usePlan } from '../plan/usePlan';
import '../doc/doc.css';
import type { Arc } from '../plan/arc';
import { EMPTY_REGISTRIES, tagTint } from '../plan/registries';
import type { Registries } from '../plan/registries';
import { THEMES, canvasGround, cardFill, markInk, tone } from '../design/tokens';
import { tagTones } from '../design/tagPalette';
import type { TagColorId } from '../design/tagPalette';
import type { Theme } from '../design/tokens';
import { useTheme } from '../design/useTheme';
import { StatusGlyph } from '../board/StatusGlyph';
import { CardTagMark, tagMarkOf } from '../folders/TagMark';
import type { TagMark } from '../folders/TagMark';
import { iconFor } from '../folders/FolderIcons';
import { hrefFor } from '../routes/EntityPage';
import { useCatalog } from '../entity/catalogStore';
import type { CatalogMap } from '../entity/catalogStore';
import { arrange, nameOf, newNookFile, newNookPath, waitingOnYou } from './workspace';
import { cardFace, sourceWords } from './cardFace';
import type { HeldSource } from './cardFace';
import { NookGlyph } from '../nook/NookGlyph';
import { CloseGlyph, NoteGlyph, PlayGlyph, WaitingMark } from '../nook/parts';
import { useWorkspaces } from './useWorkspaces';
import { Miniature, useMiniLook } from './NookCanvas';
import type { MiniLook } from './NookCanvas';
import { newUlid } from '../entity/catalog';
import type { Workspace } from './workspace';
import { Loading } from '../shell/Loading';
import { currentScratch, scratchCanvases } from './scratch';
import { useHolderWrites } from '../membership/useHolderWrites';
import { PinMenu } from './PinMenu';
import { usePinning } from './usePins';
import { deskMembers } from '../desk/desk';
import { readDeskContainer } from '../desk/deskContainer';
import type { DeskContainer } from '../desk/deskContainer';
import { useDeskContainer } from '../desk/useDeskContainer';
import './workspaces.css';

/**
 * The nooks, as places: each its card, the way the app draws one. What is on
 * the desk and what is on its shelf come first, each in a row of its own, the
 * rest under them, and the archived ones one quiet tab off — out of the way,
 * not gone.
 */

export type NooksLayout = 'recent' | 'scratch';

const NOOKS_LAYOUT_KEY = 'nooks.layout';

function readNooksLayout(): NooksLayout {
  try {
    const stored = localStorage.getItem(NOOKS_LAYOUT_KEY);
    return stored === 'scratch' ? stored : 'recent';
  } catch {
    return 'recent';
  }
}

function writeNooksLayout(layout: NooksLayout): void {
  try {
    localStorage.setItem(NOOKS_LAYOUT_KEY, layout);
  } catch {
    /* the choice is not remembered, and nothing else is lost */
  }
}

export function WorkspacesRoute() {
  const { state, reload, commit } = useVault();
  const navigate = useNavigate();
  const { plan } = usePlan();
  const catalog = useCatalog();
  const read = useWorkspaces();
  const { theme } = useTheme();
  const { newScratch } = useHolderWrites();
  const { pin, unpin, archive } = usePinning(read.status === 'ready' ? read.workspaces : []);
  const { file: deskFile } = useDeskContainer();
  const desk = useMemo(() => readDeskContainer(deskFile.status === 'read' ? deskFile.raw : null), [deskFile]);

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to read your vault.
        </p>
      </section>
    );
  }

  if (state.status === 'error') return <VaultErrorSection state={state} reload={reload} />;

  if (read.status === 'failed') {
    return (
      <section className="placeholder">
        <h1>Could not read the nooks</h1>
        <button type="button" onClick={reload}>
          Try again
        </button>
      </section>
    );
  }

  if (state.status === 'loading' || read.status === 'reading') {
    return <Loading title="Reading the nooks…" />;
  }

  /** A discrete act, so it is sent rather than queued: the nook it opens
   *  has to be there to open. A failed send stays on the list, and the sync
   *  state says why. */
  const makeNook = async () => {
    if (state.status !== 'ready') return;
    const now = new Date();
    const taken = new Set(state.vault.listPaths('spaces/'));
    const path = newNookPath(now, (candidate) => taken.has(candidate));
    try {
      await commit([{ path, text: newNookFile(newUlid(now.getTime()), now) }]);
    } catch {
      return;
    }
    navigate(hrefFor(path), { state: { newNook: true } });
  };

  return (
    <WorkspaceList
      workspaces={read.workspaces}
      desk={desk}
      arcs={plan.status === 'ready' ? plan.plan.arcs : null}
      catalog={catalog}
      registries={plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES}
      theme={theme}
      onNew={() => void makeNook()}
      onNewScratch={() => void newScratch().then((to) => to && navigate(hrefFor(to)))}
      onTogglePin={(w, pinned) => void (pinned ? unpin(w) : pin(w))}
      onArchive={(w, archived) => void archive(w, archived)}
    />
  );
}

/** [arcs] is null while the plan is still being read — a card says nothing
 *  about its arcs until then, rather than guessing. */
export function WorkspaceList({
  workspaces,
  desk = null,
  arcs,
  catalog,
  registries = EMPTY_REGISTRIES,
  theme = 'light',
  onNew,
  onNewScratch,
  onTogglePin,
  onArchive,
}: {
  workspaces: Workspace[];
  /** The desk's container, once the phone has made one: without it there is no shelf, and the desk
   *  is what the nooks themselves say. */
  desk?: DeskContainer | null;
  arcs: Arc[] | null;
  catalog: CatalogMap;
  /** Where a tag's colour comes from. */
  registries?: Registries;
  theme?: Theme;
  /** Makes a nook and opens it. Absent where one cannot be made. */
  onNew?: () => void;
  /** Makes a scratch canvas and opens it. Absent where one cannot be made. */
  onNewScratch?: () => void;
  /** Pins a nook, or unpins one that is pinned. Absent where nothing can be written. */
  onTogglePin?: (nook: Workspace, pinned: boolean) => void;
  /** Files a nook away, or brings an archived one back — for a draft, lets it
   *  go. Absent where nothing can be written. */
  onArchive?: (nook: Workspace, archived: boolean) => void;
}) {
  const [view, setView] = useState<'live' | 'away'>('live');
  const [layout, setLayout] = useState<NooksLayout>(readNooksLayout);
  const { live, away } = useMemo(() => arrange(workspaces), [workspaces]);
  const canvases = useMemo(() => scratchCanvases(workspaces), [workspaces]);
  // The desk page's own reading of the desk and the shelf; what is on them is not listed again below.
  const { onDesk, onShelf, onRows } = useMemo(() => {
    const members = deskMembers(desk, workspaces);
    const nooks = (items: typeof members.desk) => items.flatMap((item) => (item.nook ? [item.nook] : []));
    const rows = { onDesk: nooks([...members.desk, ...members.drafts]), onShelf: nooks(members.shelf) };
    return { ...rows, onRows: new Set([...rows.onDesk, ...rows.onShelf].map((w) => w.path)) };
  }, [desk, workspaces]);
  const rest = useMemo(() => live.filter((w) => !onRows.has(w.path)), [live, onRows]);
  const scratchNow = useMemo(() => currentScratch(workspaces), [workspaces]);
  // A canvas on the desk or the shelf shows there once, and leaves the slot above the list.
  const current = scratchNow && !onRows.has(scratchNow.path) ? scratchNow : null;
  const hasScratch = canvases.live.length + canvases.away.length > 0;
  const byName = useMemo(() => (arcs ? new Map(arcs.map((arc) => [arc.name, arc])) : null), [arcs]);
  const look = useMiniLook(arcs, catalog, registries, theme);
  const showing = view === 'away' ? away : rest;
  const scratchTab = view === 'live' && hasScratch && layout === 'scratch';
  const chooseLayout = (next: NooksLayout) => {
    setLayout(next);
    writeNooksLayout(next);
  };
  const card = (w: Workspace) => (
    <NookCell
      key={w.path}
      pinned={w.pinned !== null}
      archived={w.archived}
      onTogglePin={w.archived || w.draft || !onTogglePin ? undefined : () => onTogglePin(w, w.pinned !== null)}
      onArchive={!onArchive || w.draft ? undefined : () => onArchive(w, !w.archived)}
      onLetGo={w.draft && !w.archived && onArchive ? () => onArchive(w, true) : undefined}
    >
      <NookCard
        workspace={w}
        arcs={byName}
        catalog={catalog}
        hue={w.tags[0] ? tagTint(registries, w.tags[0]) : null}
        mark={w.tags[0] ? tagMarkOf([w.tags[0]], registries.folders, theme) : null}
        theme={theme}
        look={look}
      />
    </NookCell>
  );

  return (
    <section className={`nooks${view === 'away' ? ' is-away' : ''}`}>
      <header className="nooks-head">
        {(hasScratch || away.length > 0) && (
          <div className="plan-switch" role="group" aria-label="How the nooks are drawn">
            {(['recent', 'scratch', 'archived'] as const)
              .filter((option) => (option === 'scratch' ? hasScratch : option === 'archived' ? away.length > 0 : true))
              .map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={
                    option === 'archived'
                      ? view === 'away'
                      : view === 'live' && (layout === option || (option === 'recent' && !scratchTab))
                  }
                  onClick={() => {
                    if (option === 'archived') return setView('away');
                    setView('live');
                    chooseLayout(option);
                  }}
                >
                  {option === 'recent' ? 'Recent' : option === 'scratch' ? 'Scratch' : 'Archived'}
                </button>
              ))}
          </div>
        )}
        {view === 'live' && !scratchTab && onNew && (
          <button type="button" className="nooks-new" onClick={onNew}>
            <PlusGlyph /> A new nook
          </button>
        )}
        {scratchTab && onNewScratch && (
          <button type="button" className="nooks-new" onClick={onNewScratch}>
            <PlusGlyph /> A new scratch canvas
          </button>
        )}
      </header>

      {scratchTab ? (
        <>
          {canvases.live.length > 0 && <div className="nooks-grid">{canvases.live.map(card)}</div>}
          {canvases.away.length > 0 && (
            <section className="nooks-room-group">
              <h2 className="nooks-room-head">Put away</h2>
              <div className="nooks-grid">{canvases.away.map(card)}</div>
            </section>
          )}
        </>
      ) : (
        <>
          {view === 'live' && onDesk.length > 0 && (
            <section className="nooks-band" aria-label="On the desk">
              <h2 className="nooks-section-head">
                <Link to="/" className="nooks-band-link">
                  On the desk
                </Link>
              </h2>
              <div className="nooks-band-row">{onDesk.map(card)}</div>
            </section>
          )}
          {view === 'live' && onShelf.length > 0 && (
            <section className="nooks-band" aria-label="Shelf">
              <h2 className="nooks-section-head">Shelf</h2>
              <div className="nooks-band-row">{onShelf.map(card)}</div>
            </section>
          )}
          {view === 'live' && onRows.size > 0 && (current || rest.length > 0) && (
            <h2 className="nooks-section-head nooks-rest-head">Nooks</h2>
          )}
          {/* The canvas sends land in, where it is easy to find. */}
          {view === 'live' && current && <div className="nooks-grid">{card(current)}</div>}
          {showing.length > 0 && <div className="nooks-grid">{showing.map(card)}</div>}
        </>
      )}

    </section>
  );
}

/** A canvas's height for its width, as it is drawn on a nook card at its small width. */
const FULL_CANVAS_ASPECT = 150 / 280;
const STRIP_ASPECT = 56 / 254;
const RECORDING_FILE = /(?:^|\/)Recording [^/]*$/;

/** A nook's card with what ends its head — its ⋮, or a draft's × — beside the
 *  link, not inside it. */
function NookCell({
  pinned,
  archived,
  onTogglePin,
  onArchive,
  onLetGo,
  children,
}: {
  pinned: boolean;
  archived: boolean;
  onTogglePin: (() => void) | undefined;
  onArchive: (() => void) | undefined;
  /** A draft says one thing at its head's end: that it can be let go. */
  onLetGo: (() => void) | undefined;
  children: ReactNode;
}) {
  const menu = !onLetGo && (onTogglePin || onArchive);
  return (
    <div className={`nooks-cell${onLetGo || menu ? ' has-end' : ''}`}>
      {children}
      {onLetGo ? (
        <button type="button" className="nooks-cell-end nooks-let-go" aria-label="Let it go" title="Let it go" onClick={onLetGo}>
          <CloseGlyph />
        </button>
      ) : (
        menu && (
          <PinMenu
            className="nooks-cell-end nooks-cell-menu"
            pinned={pinned}
            archived={archived}
            onToggle={onTogglePin}
            onArchive={onArchive}
          />
        )
      )}
    </div>
  );
}

/**
 * One nook, a sibling of the recording's card: in its folder's card tone, its
 * head the arch in the folder's colour and the name, ending on the ? while an
 * arc it reaches waits on you. Under the head, [cardFace]: its Face, its Next,
 * its canvas strip and one secondary kind. No date, no counts, no bottom row.
 * A nook with nothing to say but its canvas is that canvas, edge to edge. A
 * draft wears the assistant's dashed edge.
 */
export function NookCard({
  inert = false,
  workspace,
  arcs,
  catalog,
  hue,
  mark = null,
  theme,
  look,
  open = false,
}: {
  /** Opened out in place, on a canvas: the same card with its lead whole, its strip the full width
   *  and more lines under it. */
  open?: boolean;
  /** Drawn as a plain block, for a holder that is itself the press target. */
  inert?: boolean;
  workspace: Workspace;
  arcs: Map<string, Arc> | null;
  catalog: CatalogMap;
  /** The nook's own tag's colour, or null for a nook in none. */
  hue: TagColorId | null;
  /** The mark of the nook's own tag, drawn at the card's foot. */
  mark?: TagMark | null;
  theme: Theme;
  look: (ref: string) => MiniLook;
}) {
  const { name, named } = nameOf(workspace, catalog);
  const face = cardFace(workspace, named, arcs, open);
  const tones = hue ? tagTones(theme, hue) : null;
  const canvas = canvasGround(theme, hue);
  const asking = waitingOnYou(workspace, arcs) > 0;
  const draft = workspace.draft && !workspace.archived;
  const style = { background: cardFill(theme, hue), '--bg': canvas.fill, '--dot': canvas.dot } as CSSProperties;
  const kindAt = (path: string) => catalog.get(path)?.kind ?? null;
  const shell = (className: string, label: string, children: ReactNode) => {
    const classes = `${className}${draft ? ' is-draft' : ''}${open ? ' is-open' : ''}`;
    return inert ? (
      <span className={classes} style={style}>
        {children}
      </span>
    ) : (
      <Link to={hrefFor(workspace.path)} className={classes} style={style} aria-label={label}>
        {children}
      </Link>
    );
  };
  if (face.fullBleed && workspace.plane.length > 0) {
    return shell(
      'nooks-card is-canvas',
      'Open an unnamed nook',
      <Miniature
        plane={workspace.plane}
        kindAt={kindAt}
        look={look}
        own={tones?.band ?? null}
        theme={theme}
        aspect={FULL_CANVAS_ASPECT}
      />,
    );
  }
  const leads = face.picks || face.next?.leads === true || face.lead !== null;
  const secondary = face.secondary;
  const words = (source: HeldSource) => sourceWords(source, catalog.get(source.path)?.name ?? null);
  return shell(
    'nooks-card',
    name ? `Open ${name}` : 'Open an unnamed nook',
    <>
      <span className="nooks-card-head">
        <NookGlyph size={16} ink={hue ? markInk(theme, hue) : 'var(--inkSoft)'} />
        {name ? <span className="nooks-card-name">{name}</span> : <i className="nooks-card-name nooks-blank">Blank</i>}
        {asking && <WaitingMark />}
      </span>
      {leads && (
        <span className="nooks-face">
          {face.picks &&
            workspace.face.map((line, i) => (
              <span key={i} className="nooks-pick">
                {line}
              </span>
            ))}
          {face.next?.leads && <span className="nooks-lead">{face.next.text}</span>}
          {face.lead && <span className="nooks-lead">{words(face.lead)}</span>}
        </span>
      )}
      {face.next && !face.next.leads && (
        <span className="nooks-next">
          <span className="nooks-next-label t-eyebrowMono">Next</span>
          <span className="nooks-next-text">{face.next.text}</span>
        </span>
      )}
      {face.strip && (
        <span className="nooks-strip">
          <Miniature
            plane={workspace.plane}
            kindAt={kindAt}
            look={look}
            own={tones?.band ?? null}
            theme={theme}
            aspect={STRIP_ASPECT}
            flat={
              tones
                ? { ground: tones.ground, chip: tones.chip, edge: tones.edge }
                : { ground: tone(theme, 0), chip: tone(theme, 2), edge: THEMES[theme].inkFaint }
            }
          />
        </span>
      )}
      {secondary.kind !== 'none' && (
        <span className="nooks-more">
          {secondary.kind === 'arcs' &&
            secondary.arcs.map((arc) => (
              <span key={arc.path} className="nooks-line">
                <span className="nooks-line-lane">
                  <StatusGlyph status={arc.status} size={9} />
                </span>
                <span className="nooks-line-text is-arc">{arc.title}</span>
              </span>
            ))}
          {secondary.kind === 'talk' && (
            <span className="nooks-line">
              <span className="nooks-line-lane">
                <ChatGlyph />
              </span>
              <span className="nooks-line-text">Pick up the talk</span>
            </span>
          )}
          {secondary.kind === 'newest' &&
            secondary.sources.map((source) => {
              const line = words(source);
              if (!line) return null;
              const recording = (kindAt(source.path) ?? (RECORDING_FILE.test(source.path) ? 'recording' : null)) === 'recording';
              return (
                <span key={source.path} className="nooks-line">
                  <span className="nooks-line-lane">{recording ? <PlayGlyph /> : <NoteGlyph />}</span>
                  <span className="nooks-line-text is-source">{line}</span>
                </span>
              );
            })}
        </span>
      )}
      {mark && (
        <span className="nooks-tag">
          <CardTagMark mark={mark} size={14} />
        </span>
      )}
    </>,
  );
}

function PlusGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

const Chat = iconFor('chat');

function ChatGlyph() {
  return Chat ? <Chat size={14} /> : null;
}
